// ヒストグラムのレンダラー実装

import { GraphRenderer, HistogramConfig, GraphCallbacks } from '../../../../types/graph-builder.types';
import { DataSet } from '../../../../types';
import { Histogram } from '../../Histogram';
import { getColumnData, getColumnById } from '../../../utils/column-utils';

export const histogramRenderer: GraphRenderer<HistogramConfig> = {
  validate(config: HistogramConfig, dataset: DataSet): string | null {
    // データセットの検証
    if (!dataset || !dataset.schema || !dataset.schema.columns) {
      return 'Invalid dataset';
    }

    // 列の存在確認
    const column = getColumnById(dataset, config.column);
    if (!column) {
      return `Column "${config.column}" not found in dataset`;
    }

    // 数値型チェック
    if (column.type !== 'int64' && column.type !== 'float64') {
      return `Column "${column.name}" must be numeric (found: ${column.type})`;
    }

    // ビン数の検証
    if (config.bins < 1 || config.bins > 100) {
      return 'Number of bins must be between 1 and 100';
    }

    // グループ化列の検証（オプション）
    if (config.groupByColumn) {
      const groupColumn = getColumnById(dataset, config.groupByColumn);
      if (!groupColumn) {
        return `Group by column "${config.groupByColumn}" not found in dataset`;
      }
      // カテゴリカル型チェック
      if (groupColumn.type !== 'string' && groupColumn.type !== 'boolean') {
        return `Group by column "${groupColumn.name}" must be categorical (found: ${groupColumn.type})`;
      }
    }

    return null;
  },

  render(config: HistogramConfig, dataset: DataSet, callbacks: GraphCallbacks) {
    const columnData = getColumnData(dataset, config.column);
    const column = getColumnById(dataset, config.column);
    
    if (!columnData || !column) {
      return <div>Error: Column data not found</div>;
    }

    // グループ化が設定されている場合
    if (config.groupByColumn) {
      const groupColumnData = getColumnData(dataset, config.groupByColumn);
      const groupColumn = getColumnById(dataset, config.groupByColumn);
      
      if (!groupColumnData || !groupColumn) {
        return <div>Error: Group column data not found</div>;
      }

      // グループごとにデータを分割
      const groupedData: { groupName: string; values: number[]; originalData: { value: number; index: number }[] }[] = [];
      const groupMap = new Map<string, { values: number[]; originalData: { value: number; index: number }[] }>();
      
      columnData.forEach((value, index) => {
        if (typeof value === 'number' && !isNaN(value)) {
          const groupValue = String(groupColumnData[index] ?? 'null');
          
          if (!groupMap.has(groupValue)) {
            groupMap.set(groupValue, { values: [], originalData: [] });
          }
          
          const group = groupMap.get(groupValue)!;
          group.values.push(value);
          group.originalData.push({ value, index });
        }
      });
      
      // MapをArrayに変換
      groupMap.forEach((data, groupName) => {
        groupedData.push({ groupName, ...data });
      });
      
      // グループ名でソート
      groupedData.sort((a, b) => a.groupName.localeCompare(b.groupName));
      

      // onBinClickハンドラーを作成
      const handleBinClick = callbacks.onElementSelect
        ? (_minValue: number, _maxValue: number, dataIndices: number[], isAdditive?: boolean) => {
            callbacks.onElementSelect!(dataIndices, isAdditive || false);
          }
        : undefined;

      return (
        <div style={{ width: '100%', height: '100%' }}>
          <Histogram
            groupedData={groupedData}
            bins={config.bins}
            title={config.title || `${column.name} by ${groupColumn.name}`}
            showDensity={config.showDensity}
            orientation={config.orientation}
            groupMode={config.groupMode || 'overlay'}
            showAnnotations={config.showAnnotations}
            onBinClick={handleBinClick}
            selectedIndices={callbacks.selectedRows}
            width={undefined} // コンテナに合わせて自動調整
            height={undefined} // コンテナに合わせて自動調整
          />
        </div>
      );
    }

    // グループ化なしの場合（既存の処理）
    const numericData: number[] = [];
    const originalData: { value: number; index: number }[] = [];
    
    columnData.forEach((value, index) => {
      if (typeof value === 'number' && !isNaN(value)) {
        numericData.push(value);
        originalData.push({ value, index });
      }
    });


    // onBinClickハンドラーを作成
    const handleBinClick = callbacks.onElementSelect
      ? (_minValue: number, _maxValue: number, dataIndices: number[], isAdditive?: boolean) => {
          callbacks.onElementSelect!(dataIndices, isAdditive || false);
        }
      : undefined;

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <Histogram
          data={numericData}
          bins={config.bins}
          title={config.title || column.name}
          showDensity={config.showDensity}
          orientation={config.orientation}
          showAnnotations={config.showAnnotations}
          originalData={originalData}
          onBinClick={handleBinClick}
          selectedIndices={callbacks.selectedRows}
          width={undefined} // コンテナに合わせて自動調整
          height={undefined} // コンテナに合わせて自動調整
        />
      </div>
    );
  }
};